import { CurrencyPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslocoService } from '@ngneat/transloco';
import { Subscription } from 'rxjs';
import { CommonConstant } from 'src/app/constants';

import {
  BarcodeReaderEventType,
  BarcodeResult,
  BaseMenuItem,
  PriceType
} from 'src/app/models';
import { TicketProvider } from 'src/app/providers';
import { BarcodeProvider } from 'src/app/providers/barcode.provider';
import { LogRocketProvider } from 'src/app/providers/logrocket.provider';
import { ModalService } from 'src/app/services';

enum GiftCardItemModalView {
  LIST = 'list',
  CUSTOM_PRICE = 'custom-price',
  SCAN = 'scan',
  MANUAL = 'manual',
  SUMMARY = 'summary'
}

@Component({
  selector: 'pos-gift-card-item-modal',
  templateUrl: './gift-card-item.modal.html',
  styleUrls: ['./gift-card-item.modal.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CurrencyPipe]
})
export class GiftCardItemModal implements OnInit, OnDestroy {
  @Input() menuItem: BaseMenuItem;
  @Input() parentUuid?: string;

  view = GiftCardItemModalView.LIST;
  amount: number | string;
  price: number;
  isVariable = false;
  gift_card_code: string;
  barcodeSubscription: Subscription;

  constructor(
    private readonly modalController: ModalController,
    private readonly barcodeProvider: BarcodeProvider,
    private readonly ticketProvider: TicketProvider,
    private readonly logRocketProvider: LogRocketProvider,
    private readonly cdr: ChangeDetectorRef,
    private readonly modalService: ModalService,
    private readonly currencyPipe: CurrencyPipe,
    private readonly translocoService: TranslocoService
  ) {}

  ngOnInit() {
    this.price = this.menuItem.price;
    this.isVariable = this.menuItem.price_type === PriceType.VARIABLE;
    if (!this.isVariable) {
      this.view = GiftCardItemModalView.SCAN;
    }

    this.barcodeSubscription = this.barcodeProvider.subscribe(
      BarcodeReaderEventType.GIFT_CARD,
      (barcode: BarcodeResult) => {
        this.logRocketProvider.log(
          '[Gift Card Item Modal] BarcodeReaderEventType.GIFT_CARD barcode',
          JSON.stringify(barcode)
        );
        if (this.view === GiftCardItemModalView.SCAN) {
          this.gift_card_code = barcode.barcode;
          this.view = GiftCardItemModalView.SUMMARY;
          this.cdr.markForCheck();
        }
      }
    );
  }

  ngOnDestroy() {
    this.barcodeProvider.unsubscribe(this.barcodeSubscription);
  }

  onSelectGiftCard(price: number = null) {
    this.price = price;
    this.view = !!price
      ? GiftCardItemModalView.SCAN
      : GiftCardItemModalView.CUSTOM_PRICE;
  }

  onConfirmCustomAmount() {
    const amount = +this.amount,
      MAX_VARIABLE_VALUE = CommonConstant.MAX_VARIABLE_PRICE_INPUT_VALUE;

    if (amount > MAX_VARIABLE_VALUE) {
      this.modalService.alert({
        message: this.translocoService.translate('posManager.invalidDollarAmount', {
          maxValue: this.currencyPipe.transform(
            MAX_VARIABLE_VALUE,
            'USD',
            'symbol-narrow',
            '1.0-2'
          )
        }),
        buttons: [this.translocoService.translate('common.ok')]
      });
      return;
    }

    this.view = GiftCardItemModalView.SCAN;
    this.price = amount;
    this.amount = null;
  }

  onManualGiftCardEntry() {
    this.view = GiftCardItemModalView.MANUAL;
  }

  onConfirmGiftCardNumber() {
    this.view = GiftCardItemModalView.SUMMARY;
  }

  onAddToTicket() {
    this.menuItem.price = this.price;
    this.menuItem.gift_card_code = this.gift_card_code;
    this.ticketProvider.ticketItemAddingHandler(this.menuItem, this.parentUuid);
    this.modalController.dismiss();
  }

  onBack() {
    if (
      this.view === GiftCardItemModalView.CUSTOM_PRICE ||
      (this.view === GiftCardItemModalView.SCAN && this.isVariable)
    ) {
      this.price = null;
      this.view = GiftCardItemModalView.LIST;
    } else if (
      this.view === GiftCardItemModalView.MANUAL ||
      this.view === GiftCardItemModalView.SUMMARY
    ) {
      this.view = GiftCardItemModalView.SCAN;
    }
  }

  onCloseModal() {
    this.modalController.dismiss();
  }
}
